import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchSavedFilterViews as fetchApi,
  createSavedFilterView as createApi,
  updateSavedFilterView as updateApi,
  duplicateSavedFilterView as duplicateApi,
  deleteSavedFilterView as deleteApi,
} from "../api/savedFilterViewsApi";

const initialState = {
  views: [],
  loading: false,
  saving: false,
  error: null,
};

export const fetchSavedFilterViews = createAsyncThunk(
  "savedFilterViews/fetchAll",
  /** @param {{ id_client: any }} arg */
  async ({ id_client }, { rejectWithValue }) => {
    try {
      return await fetchApi({ id_client });
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch saved filter views");
    }
  }
);

export const createSavedFilterView = createAsyncThunk(
  "savedFilterViews/create",
  async (payload, { rejectWithValue }) => {
    try {
      return await createApi(payload);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to create saved filter view");
    }
  }
);

export const updateSavedFilterView = createAsyncThunk(
  "savedFilterViews/update",
  async (payload, { rejectWithValue }) => {
    try {
      return await updateApi(payload);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to update saved filter view");
    }
  }
);

export const duplicateSavedFilterView = createAsyncThunk(
  "savedFilterViews/duplicate",
  async (payload, { rejectWithValue }) => {
    try {
      return await duplicateApi(payload);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to duplicate saved filter view");
    }
  }
);

export const deleteSavedFilterView = createAsyncThunk(
  "savedFilterViews/delete",
  async (payload, { rejectWithValue }) => {
    try {
      await deleteApi(payload);
      return payload.id_view;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to delete saved filter view");
    }
  }
);

const savedFilterViewsSlice = createSlice({
  name: "savedFilterViews",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSavedFilterViews.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSavedFilterViews.fulfilled, (state, action) => {
        state.loading = false;
        state.views = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchSavedFilterViews.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(createSavedFilterView.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createSavedFilterView.fulfilled, (state, action) => {
        state.saving = false;
        if (action.payload) {
          state.views.unshift(action.payload);
        }
      })
      .addCase(createSavedFilterView.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })

      .addCase(updateSavedFilterView.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateSavedFilterView.fulfilled, (state, action) => {
        state.saving = false;
        if (action.payload) {
          const idx = state.views.findIndex(
            (v) => v.id_saved_filter_view === action.payload.id_saved_filter_view
          );
          if (idx !== -1) state.views[idx] = action.payload;
        }
      })
      .addCase(updateSavedFilterView.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })

      .addCase(duplicateSavedFilterView.fulfilled, (state, action) => {
        if (action.payload) {
          state.views.unshift(action.payload);
        }
      })

      .addCase(deleteSavedFilterView.fulfilled, (state, action) => {
        state.views = state.views.filter(
          (v) => v.id_saved_filter_view !== action.payload
        );
      });
  },
});

export const { clearError } = savedFilterViewsSlice.actions;
export default savedFilterViewsSlice.reducer;
